import { UniversalCta } from '@/components';
import { HeaderText } from '@/components/ui';
import { HeaderSize } from '@/components/ui/HeaderText';
import { slugBuilder } from '@/core/contentfulConfig';
import { TypeLink_internalFields } from '@/core/generated-types';
import { Asset, Entry, EntryFields } from 'contentful';
import Image from 'next/image';
import { useRouter } from '@/navigation';
import React from 'react';
import classNames from 'classnames';

export interface ShopAllBannerProps {
  collectionName: EntryFields.Symbol;
  bannerTitle: string;
  bannerText: string;
  bannerCTA?: Entry<TypeLink_internalFields> | undefined;
  headerImage: Asset;
  mobileHeaderImage: Asset;
  hideGradient?: boolean;
  textColor?: 'light' | 'dark';
}

const ShopAllBanner: React.FC<ShopAllBannerProps> = ({
  collectionName,
  bannerTitle,
  bannerText,
  bannerCTA,
  headerImage,
  mobileHeaderImage,
  hideGradient,
  textColor = 'light',
}) => {
  const { push } = useRouter();
  const handleClick = () => {
    if (bannerCTA) push(slugBuilder(bannerCTA));
  };
  return (
    <>
      <HeaderText
        size={HeaderSize.H1}
        text={collectionName}
        classes="text-center mt-11 mb-9 lg:mt-20"
      />
      <div className="mx-auto px-4 md:max-w-[46.75rem] mb-6 lg:mb-8 lg:max-w-[70.5rem]">
        <div
          onClick={handleClick}
          className="relative mx-auto min-h-[350px] cursor-pointer rounded overflow-hidden max-w-[20.5rem] md:w-full md:max-w-[46.75rem] lg:max-w-[70.5rem]"
        >
          <div className="absolute z-1 w-full h-full ">
            <Image
              alt={headerImage.fields.title}
              src={`https:${headerImage.fields.file.url}`}
              fill
              quality={90}
              style={{
                objectFit: 'cover',
              }}
              className="hidden md:block"
            />
            <Image
              alt={headerImage.fields.title}
              src={`https:${mobileHeaderImage.fields.file.url}`}
              fill
              quality={90}
              style={{
                objectFit: 'cover',
              }}
              className="block md:hidden"
            />
          </div>
          {!hideGradient && (
            <div className="absolute z-2 w-full h-full bg-gradient-to-r from-black/40 via-transparent to-transparent" />
          )}
          <div
            data-testid="banner-container"
            className={classNames(
              'relative z-3 w-full h-full min-h-[350px] flex flex-col items-start pl-[15px] pt-[242px] md:pl-[84px] md:pt-[94px] pb-10',
              {
                'text-white': textColor === 'light',
                'text-charcoal-v2': textColor === 'dark',
              },
            )}
          >
            <h3 className="md:text-[32px] font-[700] text-2xl md:leading-10 lg:mb-1">
              {bannerTitle}
            </h3>
            <p className="md:h-[48px] text-base font-[400] leading-6">
              {bannerText}
            </p>
            <div className="border-0 px-0 w-[220px]">
              {bannerCTA && (
                <UniversalCta
                  link={bannerCTA}
                  className="text-[16px] px-0 h-[48px]"
                  defaultButtonStyle="Primary"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShopAllBanner;
